import { MoreHorizOutlined } from '@mui/icons-material';
import { Badge, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { Fragment, useId, useState, type MouseEvent } from 'react';
import type { AccountDescriptor } from './navigation';
import { designTokens } from './tokens';
import { IdentityAvatar } from './patterns/IdentityAvatar';
import { createFocusRingStyles } from './interaction';

const accountActionStyle = {
  width: designTokens.layout.pageHeaderControlSize,
  height: designTokens.layout.pageHeaderControlSize,
  borderRadius: designTokens.radius.circle,
  [designTokens.media.touchTarget]: { width: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize },
  '&.Mui-focusVisible, &:focus-visible': createFocusRingStyles(),
} as const;

export function AccountDock({ account, compact = false }: { account: AccountDescriptor; compact?: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuID = useId();
  const triggerID = `${menuID}-trigger`;
  const openMenu = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);
  const closeMenu = () => setAnchor(null);
  const menu = <Menu
    anchorEl={anchor}
    open={Boolean(anchor)}
    onClose={closeMenu}
    anchorOrigin={compact ? { vertical: 'bottom', horizontal: 'right' } : { vertical: -designTokens.layout.menuAnchorGap, horizontal: 'right' }}
    transformOrigin={compact ? { vertical: -designTokens.layout.menuAnchorGap, horizontal: 'right' } : { vertical: 'bottom', horizontal: 'left' }}
    slotProps={{
      paper: {
        sx: {
          width: 288,
          maxWidth: `calc(100vw - ${designTokens.layout.popupViewportInset * 2}px)`,
        },
      },
      list: { id: menuID, 'aria-labelledby': triggerID },
    }}
  >
    {compact && <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', px: 1.5, py: 1.5 }}>
      <IdentityAvatar src={account.avatarUrl} sx={{ width: 44, height: 44, fontSize: 16, fontWeight: 750 }}>{account.avatarLabel || Array.from(account.name)[0]}</IdentityAvatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" noWrap sx={{ color: designTokens.color.primaryText, fontWeight: 750 }}>{account.name}</Typography>
        {account.organization && <Typography variant="caption" noWrap sx={{ display: 'block', color: designTokens.color.secondaryText }}>{account.organization}</Typography>}
      </Box>
    </Stack>}
    {compact && <Divider sx={{ mx: 1.5, mb: 0.75 }} />}
    {account.actions?.map((action) => <Fragment key={action.id}>
      {action.dividerBefore && <Divider sx={{ mx: 1.5, my: 0.75 }} />}
      <MenuItem
        onClick={() => { closeMenu(); action.onSelect(); }}
        sx={action.destructive ? { color: designTokens.color.dangerText } : undefined}
      >
        {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
        {action.label}
      </MenuItem>
    </Fragment>)}
  </Menu>;
  if (compact) {
    return <Box data-testid="account-dock" sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
      <Tooltip title={account.organization ? `${account.name} · ${account.organization}` : account.name} placement="right" arrow>
        <IconButton id={triggerID} aria-label="계정 메뉴" aria-haspopup="menu" aria-controls={anchor ? menuID : undefined} aria-expanded={Boolean(anchor)} onClick={openMenu} sx={{ ...accountActionStyle, p: 0.5, bgcolor: anchor ? designTokens.color.actionSoft : undefined }}>
          <Badge overlap="circular" variant="dot" color="success" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}><IdentityAvatar src={account.avatarUrl} sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 750 }}>{account.avatarLabel || Array.from(account.name)[0]}</IdentityAvatar></Badge>
        </IconButton>
      </Tooltip>
      {menu}
    </Box>;
  }
  return <Stack direction="row" data-testid="account-dock" sx={{ alignItems: 'center', minHeight: 64, px: 1.5, py: 1, borderTop: '1px solid', borderColor: designTokens.color.divider, bgcolor: designTokens.color.canvas }} spacing={1}>
    <IdentityAvatar src={account.avatarUrl} sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 750 }}>{account.avatarLabel || Array.from(account.name)[0]}</IdentityAvatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ color: designTokens.color.primaryText, fontWeight: 700 }}>{account.name}</Typography>
      {account.organization && <Typography variant="caption" noWrap sx={{ display: 'block', color: designTokens.color.secondaryText, mt: 0.15 }}>{account.organization}</Typography>}
    </Box>
    {account.actions?.length ? <>
      <IconButton id={triggerID} size="small" aria-label="계정 메뉴" aria-haspopup="menu" aria-controls={anchor ? menuID : undefined} aria-expanded={Boolean(anchor)} onClick={openMenu} sx={accountActionStyle}><MoreHorizOutlined fontSize="small" /></IconButton>
      {menu}
    </> : null}
  </Stack>;
}
